import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  FiDollarSign, 
  FiMail, 
  FiMessageSquare, 
  FiSave, 
  FiRefreshCw, 
  FiClock,
  FiUsers,
  FiPercent,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiEye,
  FiX,
  FiRotateCcw,
  FiPackage,
  FiTag,
  FiArrowRight,
  FiToggleLeft,
  FiToggleRight,
  FiCreditCard,
  FiTrendingUp,
  FiAlertCircle,
  FiPower,
  FiCheckCircle,
  FiMinus,
  FiMaximize,
  FiShoppingCart
} from 'react-icons/fi';
import TenantWalletManager from './TenantWalletManager';

const PricingManagement = () => {
  // ============================================
  // STATE
  // ============================================
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [, setPricingHistory] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showOverrideDetails, setShowOverrideDetails] = useState(false);
  const [showWalletManager, setShowWalletManager] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('global');
  const [showBulkToggleModal, setShowBulkToggleModal] = useState(false);
  const [selectedBulkPricing, setSelectedBulkPricing] = useState(null);
  
  // ============================================
  // FORM STATE
  // ============================================
  
  const [formData, setFormData] = useState({
    emailPricePerCredit: 0.20,
    smsPricePerCredit: 0.15,
    currency: 'INR',
    currencySymbol: '₹',
    currencyCode: 'INR',
    isTaxInclusive: true,
    taxPercentage: 18,
    taxDescription: 'GST',
    minEmailPurchase: 100,
    minSmsPurchase: 100,
    minEmailPurchaseAmount: 0,
    minSmsPurchaseAmount: 0,
    maxEmailPurchase: 100000,
    maxSmsPurchase: 100000,
    maxEmailPurchaseAmount: 0,
    maxSmsPurchaseAmount: 0,
    changeReason: '',
    notes: ''
  });
  
  const [tenantForm, setTenantForm] = useState({
    communicationType: 'email',
    discountType: 'percentage',
    discountValue: 0,
    fixedPrice: null,
    validFrom: '',
    validTo: '',
    description: '',
    isTaxInclusive: null,
    taxPercentage: null,
    taxDescription: null
  });
  
  const [bulkForm, setBulkForm] = useState({
    communicationType: 'email',
    minCredits: 10000,
    specialPrice: 0.10,
    validFrom: '',
    validTo: '',
    description: ''
  });

  const [bundleForm, setBundleForm] = useState({
    name: '',
    description: '',
    price: 0,
    emailCredits: 0,
    smsCredits: 0,
    taxType: 'inclusive',
    taxPercentage: 18,
    taxDescription: 'GST',
    displayOrder: 0,
    badge: '',
    icon: '📦',
    features: [],
    recommendedFor: 'all',
    isActive: true
  });

  // ============================================
  // FETCH DATA
  // ============================================
  
const fetchPricing = async () => {
  try {
    setLoading(true);
    
    const [pricingRes, historyRes, tenantsRes, auditRes, bundlesRes] = await Promise.all([
      api.get('/api/super-admin/pricing'),
      api.get('/api/super-admin/pricing/history?limit=10'),
      api.get('/api/super-admin/tenant-pricing?limit=50'),
      api.get('/api/super-admin/audit?limit=50'),
      api.get('/api/super-admin/bundles?active=true')
    ]);
    
    if (pricingRes.data.success) {
      const data = pricingRes.data.data;
      setPricing(data);
      setFormData({
        emailPricePerCredit: data.emailPricePerCredit,
        smsPricePerCredit: data.smsPricePerCredit,
        currency: data.currency || 'INR',
        currencySymbol: data.currencySymbol || '₹',
        currencyCode: data.currencyCode || 'INR',
        isTaxInclusive: data.isTaxInclusive !== undefined ? data.isTaxInclusive : true,
        taxPercentage: data.taxPercentage || 0,
        taxDescription: data.taxDescription || '',
        minEmailPurchase: data.minEmailPurchase || 100,
        minSmsPurchase: data.minSmsPurchase || 100,
        minEmailPurchaseAmount: data.minEmailPurchaseAmount || 0,
        minSmsPurchaseAmount: data.minSmsPurchaseAmount || 0,
        maxEmailPurchase: data.maxEmailPurchase || 100000,
        maxSmsPurchase: data.maxSmsPurchase || 100000,
        maxEmailPurchaseAmount: data.maxEmailPurchaseAmount || 0,  
        maxSmsPurchaseAmount: data.maxSmsPurchaseAmount || 0,      
        changeReason: '',
        notes: data.notes || ''
      });
    }
      
      if (historyRes.data.success) {
        setPricingHistory(historyRes.data.data);
      }
      
      if (tenantsRes.data.success) {
        setTenants(tenantsRes.data.data);
      }
      
      if (auditRes.data.success) {
        setAuditLogs(auditRes.data.data);
      }
      
      if (bundlesRes.data.success) {
        setBundles(bundlesRes.data.data);
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      alert('Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  // ============================================
  // RENDER HELPERS
  // ============================================
  
  const getCurrencySymbol = () => {
    if (pricing?.currencySymbol) {
      return pricing.currencySymbol;
    }
    return '₹';
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) {
      return `${getCurrencySymbol()}0.00`;
    }
    
    const numPrice = Number(price);
    const absPrice = Math.abs(numPrice);
    
    if (absPrice < 0.001 && absPrice > 0) {
      return `${getCurrencySymbol()}${numPrice.toFixed(4)}`;
    }
    if (absPrice < 0.01 && absPrice > 0) {
      return `${getCurrencySymbol()}${numPrice.toFixed(3)}`;
    }
    
    return `${getCurrencySymbol()}${numPrice.toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const formatAction = (action) => {
    const actionMap = {
      'price_updated': 'Price Updated',
      'discount_applied': 'Discount Applied',
      'discount_removed': 'Discount Removed',
      'bulk_pricing_added': 'Bulk Pricing Added',
      'bulk_pricing_removed': 'Bulk Pricing Removed',
      'bulk_pricing_toggled': 'Bulk Pricing Toggled',
      'bulk_pricing_deleted': 'Bulk Pricing Deleted',
      'tenant_pricing_reset': 'Pricing Reset',
      'bundle_created': 'Bundle Created',
      'bundle_updated': 'Bundle Updated',
      'bundle_deleted': 'Bundle Deleted',
      'bundle_toggled': 'Bundle Toggled',
      'credits_adjusted': 'Credits Adjusted',
      'credits_refunded': 'Credits Refunded',
      'credits_cleared': 'Credits Cleared'
    };
    return actionMap[action] || action.replace(/_/g, ' ').toUpperCase();
  };
  
  const getActionColor = (action) => {
    const colorMap = {
      'price_updated': 'bg-blue-100 text-blue-700',
      'discount_applied': 'bg-green-100 text-green-700',
      'discount_removed': 'bg-red-100 text-red-700',
      'bulk_pricing_added': 'bg-purple-100 text-purple-700',
      'bulk_pricing_removed': 'bg-orange-100 text-orange-700',
      'bulk_pricing_toggled': 'bg-amber-100 text-amber-700',
      'bulk_pricing_deleted': 'bg-rose-100 text-rose-700',
      'tenant_pricing_reset': 'bg-yellow-100 text-yellow-700',
      'bundle_created': 'bg-emerald-100 text-emerald-700',
      'bundle_updated': 'bg-indigo-100 text-indigo-700',
      'bundle_deleted': 'bg-rose-100 text-rose-700',
      'bundle_toggled': 'bg-amber-100 text-amber-700',
      'credits_adjusted': 'bg-cyan-100 text-cyan-700',
      'credits_refunded': 'bg-emerald-100 text-emerald-700',
      'credits_cleared': 'bg-rose-100 text-rose-700'
    };
    return colorMap[action] || 'bg-gray-100 text-gray-700';
  };

  // ============================================
  // HANDLERS
  // ============================================
  
  const handlePricingUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await api.put('/api/super-admin/pricing', {
        emailPricePerCredit: parseFloat(formData.emailPricePerCredit),
        smsPricePerCredit: parseFloat(formData.smsPricePerCredit),
        currency: formData.currency,
        currencySymbol: formData.currencySymbol,
        currencyCode: formData.currencyCode,
        isTaxInclusive: formData.isTaxInclusive,
        taxPercentage: parseFloat(formData.taxPercentage) || 0,
        taxDescription: formData.taxDescription,
        minEmailPurchase: parseInt(formData.minEmailPurchase) || 100,
        minSmsPurchase: parseInt(formData.minSmsPurchase) || 100,
        minEmailPurchaseAmount: parseFloat(formData.minEmailPurchaseAmount) || 0,
        minSmsPurchaseAmount: parseFloat(formData.minSmsPurchaseAmount) || 0,
        maxEmailPurchase: parseInt(formData.maxEmailPurchase) || 100000,
        maxSmsPurchase: parseInt(formData.maxSmsPurchase) || 100000,
        maxEmailPurchaseAmount: parseFloat(formData.maxEmailPurchaseAmount) || 0,
        maxSmsPurchaseAmount: parseFloat(formData.maxSmsPurchaseAmount) || 0,
        changeReason: formData.changeReason,
        notes: formData.notes
      });
      
      if (response.data.success) {
        alert('Pricing updated successfully!');
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error updating pricing:', error);
      alert('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTenantOverride = async (tenantId) => {
    setSaving(true);
    
    try {
      const overrideData = {
        overrides: [{
          communicationType: tenantForm.communicationType,
          discountType: tenantForm.discountType,
          discountValue: parseFloat(tenantForm.discountValue) || 0,
          fixedPrice: tenantForm.fixedPrice ? parseFloat(tenantForm.fixedPrice) : null,
          validFrom: tenantForm.validFrom || new Date().toISOString().split('T')[0],
          validTo: tenantForm.validTo || null,
          description: tenantForm.description
        }],
        changeReason: tenantForm.description || 'Admin override'
      };

      if (tenantForm.isTaxInclusive !== null && tenantForm.isTaxInclusive !== undefined) {
        overrideData.isTaxInclusive = tenantForm.isTaxInclusive;
      }
      if (tenantForm.taxPercentage !== null && tenantForm.taxPercentage !== undefined) {
        overrideData.taxPercentage = parseFloat(tenantForm.taxPercentage);
      }
      if (tenantForm.taxDescription !== null && tenantForm.taxDescription !== undefined && tenantForm.taxDescription.trim() !== '') {
        overrideData.taxDescription = tenantForm.taxDescription.trim();
      }

      const response = await api.put(`/api/super-admin/tenant-pricing/${tenantId}`, overrideData);
      
      if (response.data.success) {
        alert('Tenant pricing updated successfully!');
        setShowTenantModal(false);
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error updating tenant pricing:', error);
      alert(error.response?.data?.message || 'Failed to update tenant pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleBulkPricing = async (tenantId) => {
    setSaving(true);
    
    try {
      const response = await api.post(`/api/super-admin/tenant-pricing/bulk/${tenantId}`, {
        communicationType: bulkForm.communicationType,
        minCredits: parseInt(bulkForm.minCredits),
        specialPrice: parseFloat(bulkForm.specialPrice),
        validFrom: bulkForm.validFrom || new Date().toISOString().split('T')[0],
        validTo: bulkForm.validTo || null,
        description: bulkForm.description
      });
      
      if (response.data.success) {
        alert('Bulk pricing added successfully!');
        setShowBulkModal(false);
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error adding bulk pricing:', error);
      alert('Failed to add bulk pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleBulkPricing = async (tenantId, bulkPricingId) => {
    if (!window.confirm('Are you sure you want to toggle this bulk pricing status?')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post(`/api/super-admin/tenant-pricing/bulk/${tenantId}/toggle`, {
        bulkPricingId
      });
      
      if (response.data.success) {
        alert(`Bulk pricing ${response.data.data.isActive ? 'activated' : 'deactivated'} successfully`);
        setShowBulkToggleModal(false);
        setSelectedBulkPricing(null);
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error toggling bulk pricing:', error);
      alert(error.response?.data?.message || 'Failed to toggle bulk pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBulkPricing = async (tenantId, bulkPricingId) => {
    if (!window.confirm('Are you sure you want to delete this bulk pricing? This action cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.delete(`/api/super-admin/tenant-pricing/bulk/${tenantId}/${bulkPricingId}`);
      
      if (response.data.success) {
        alert('Bulk pricing deleted successfully');
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error deleting bulk pricing:', error);
      alert(error.response?.data?.message || 'Failed to delete bulk pricing');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetPricing = async (tenantId, tenantName) => {
    if (!window.confirm(`Are you sure you want to reset all pricing overrides for ${tenantName}? This cannot be undone.`)) {
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post(`/api/super-admin/tenant-pricing/${tenantId}/reset`);
      if (response.data.success) {
        alert(`Pricing reset successfully for ${tenantName}`);
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error resetting pricing:', error);
      alert('Failed to reset pricing');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // BUNDLE HANDLERS
  // ============================================
  
  const handleCreateBundle = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await api.post('/api/super-admin/bundles', {
        ...bundleForm,
        features: bundleForm.features.filter(f => f.trim() !== '')
      });
      
      if (response.data.success) {
        alert('Bundle created successfully!');
        setShowBundleModal(false);
        setBundleForm({
          name: '',
          description: '',
          price: 0,
          emailCredits: 0,
          smsCredits: 0,
          taxType: 'inclusive',
          taxPercentage: 18,
          taxDescription: 'GST',
          displayOrder: 0,
          badge: '',
          icon: '📦',
          features: [],
          recommendedFor: 'all',
          isActive: true
        });
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error creating bundle:', error);
      alert(error.response?.data?.message || 'Failed to create bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBundle = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await api.put(`/api/super-admin/bundles/${selectedBundle._id}`, {
        ...bundleForm,
        features: bundleForm.features.filter(f => f.trim() !== '')
      });
      
      if (response.data.success) {
        alert('Bundle updated successfully!');
        setShowBundleModal(false);
        setSelectedBundle(null);
        setBundleForm({
          name: '',
          description: '',
          price: 0,
          emailCredits: 0,
          smsCredits: 0,
          taxType: 'inclusive',
          taxPercentage: 18,
          taxDescription: 'GST',
          displayOrder: 0,
          badge: '',
          icon: '📦',
          features: [],
          recommendedFor: 'all',
          isActive: true
        });
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error updating bundle:', error);
      alert(error.response?.data?.message || 'Failed to update bundle');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBundle = async (bundleId, currentStatus) => {
    try {
      const response = await api.post(`/api/super-admin/bundles/${bundleId}/toggle`);
      if (response.data.success) {
        alert(`Bundle ${currentStatus ? 'deactivated' : 'activated'} successfully`);
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error toggling bundle:', error);
      alert('Failed to toggle bundle');
    }
  };

  const handleDeleteBundle = async (bundleId, bundleName) => {
    if (!window.confirm(`Are you sure you want to delete "${bundleName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await api.delete(`/api/super-admin/bundles/${bundleId}`);
      if (response.data.success) {
        alert('Bundle deleted successfully');
        fetchPricing();
      }
    } catch (error) {
      console.error('❌ Error deleting bundle:', error);
      alert('Failed to delete bundle');
    }
  };

  // ============================================
  // LOADING STATE
  // ============================================
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-500">Loading pricing...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================
  
  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1.5 bg-indigo-50 rounded-lg sm:hidden">
                <FiDollarSign className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-sm sm:text-base font-bold text-slate-900 truncate">Pricing</h1>
                <p className="text-[10px] text-slate-400 hidden sm:block">Manage rates & packages</p>
              </div>
            </div>
            <button
              onClick={fetchPricing}
              className="p-2 sm:p-2.5 bg-slate-100 rounded-lg text-slate-600 hover:bg-slate-200 transition-colors"
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE BOTTOM NAVIGATION */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 sm:hidden">
        <div className="flex items-center justify-around px-1">
          {[
            { id: 'global', label: 'Rates', icon: <FiDollarSign className="w-4 h-4" /> },
            { id: 'tenants', label: 'Tenants', icon: <FiUsers className="w-4 h-4" /> },
            { id: 'bundles', label: 'Bundles', icon: <FiPackage className="w-4 h-4" /> },
            { id: 'wallets', label: 'Wallets', icon: <FiCreditCard className="w-4 h-4" /> },
            { id: 'history', label: 'History', icon: <FiClock className="w-4 h-4" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-2 min-w-[40px] transition-colors relative ${
                activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {tab.icon}
              <span className={`text-[8px] font-medium mt-0.5 ${
                activeTab === tab.id ? 'text-indigo-600' : 'text-slate-400'
              }`}>
                {tab.label}
              </span>
              {activeTab === tab.id && (
                <span className="absolute -top-[1px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-indigo-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* DESKTOP TABS */}
      <div className="hidden sm:block bg-white border-b border-slate-200 sticky top-14 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2">
            {[
              { id: 'global', label: 'Global Rates', icon: <FiDollarSign className="w-4 h-4" /> },
              { id: 'tenants', label: 'Tenant Overrides', icon: <FiUsers className="w-4 h-4" /> },
              { id: 'bundles', label: 'Bundles', icon: <FiPackage className="w-4 h-4" /> },
              { id: 'wallets', label: 'Tenant Wallets', icon: <FiCreditCard className="w-4 h-4" /> },
              { id: 'history', label: 'Audit Log', icon: <FiClock className="w-4 h-4" /> }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-indigo-50 text-indigo-700' 
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============================================
          CONTENT
          ============================================ */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
        
        {/* ============================================
            TAB 1: GLOBAL PRICING
            ============================================ */}
        {activeTab === 'global' && pricing && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
{/* Quick Stats */}
<div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Email Price</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">{formatPrice(pricing.emailPricePerCredit)}</p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">SMS Price</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">{formatPrice(pricing.smsPricePerCredit)}</p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Currency</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">{pricing.currencySymbol} {pricing.currencyCode}</p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Tax</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">{pricing.taxPercentage || 0}%</p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Tax Type</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">
      {pricing.isTaxInclusive !== undefined ? (pricing.isTaxInclusive ? 'Inclusive' : 'Exclusive') : 'Inclusive'}
    </p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Min Purchase</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">{pricing.minEmailPurchase || 100}</p>
  </div>
  <div className="bg-slate-50 rounded-lg p-3">
    <p className="text-[10px] text-slate-500 font-medium">Max Amount</p>
    <p className="text-base sm:text-lg font-bold text-slate-900">
      {pricing.maxEmailPurchaseAmount > 0 ? formatPrice(pricing.maxEmailPurchaseAmount) : '∞'}
    </p>
  </div>
</div>
            
            <form onSubmit={handlePricingUpdate}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Email Price */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    <FiMail className="inline mr-1" /> Email Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                      {getCurrencySymbol()}
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={formData.emailPricePerCredit}
                      onChange={(e) => setFormData({...formData, emailPricePerCredit: e.target.value})}
                      className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5">Price per email credit</p>
                </div>
                
                {/* SMS Price */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    <FiMessageSquare className="inline mr-1" /> SMS Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                      {getCurrencySymbol()}
                    </span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={formData.smsPricePerCredit}
                      onChange={(e) => setFormData({...formData, smsPricePerCredit: e.target.value})}
                      className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5">Price per SMS credit</p>
                </div>
                
                {/* Currency */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Currency</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="text"
                      placeholder="INR"
                      value={formData.currency}
                      onChange={(e) => setFormData({...formData, currency: e.target.value})}
                      className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      placeholder="₹"
                      value={formData.currencySymbol}
                      onChange={(e) => setFormData({...formData, currencySymbol: e.target.value})}
                      className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      placeholder="INR"
                      value={formData.currencyCode}
                      onChange={(e) => setFormData({...formData, currencyCode: e.target.value})}
                      className="px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                {/* Tax % */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    <FiPercent className="inline mr-1" /> Tax %
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.taxPercentage}
                    onChange={(e) => setFormData({...formData, taxPercentage: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                {/* Tax Description */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Tax Description</label>
                  <input
                    type="text"
                    placeholder="e.g., GST"
                    value={formData.taxDescription}
                    onChange={(e) => setFormData({...formData, taxDescription: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                {/* Tax Type Toggle */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    <FiCheckCircle className="inline mr-1" /> Tax Type
                  </label>
                  <div className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg bg-slate-50">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, isTaxInclusive: true})}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                        formData.isTaxInclusive 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Inclusive
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, isTaxInclusive: false})}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                        !formData.isTaxInclusive 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      Exclusive
                    </button>
                    <div className="text-[9px] text-slate-400 max-w-[80px]">
                      {formData.isTaxInclusive ? 'Tax included' : 'Tax added on top'}
                    </div>
                  </div>
                </div>

                {/* ============================================
                    PURCHASE LIMITS SECTION - ADD UI HERE
                    ============================================ */}
                <div className="sm:col-span-2">
                  <div className="border-t border-slate-200 pt-4 mt-2">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <FiShoppingCart className="w-3.5 h-3.5" />
                      Purchase Limits
                    </p>
                    
                    {/* Row 1: Min Credits */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Min Email Credits */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMinus className="inline mr-1 text-blue-500" /> Min Email Credits
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          value={formData.minEmailPurchase}
                          onChange={(e) => setFormData({...formData, minEmailPurchase: e.target.value})}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="100"
                        />
                        <p className="text-[9px] text-slate-400 mt-0.5">Minimum credits per purchase</p>
                      </div>

                      {/* Min SMS Credits */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMinus className="inline mr-1 text-purple-500" /> Min SMS Credits
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          value={formData.minSmsPurchase}
                          onChange={(e) => setFormData({...formData, minSmsPurchase: e.target.value})}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="100"
                        />
                        <p className="text-[9px] text-slate-400 mt-0.5">Minimum credits per purchase</p>
                      </div>
                    </div>

                    {/* Row 2: Max Credits */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      {/* Max Email Credits */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMaximize className="inline mr-1 text-blue-500" /> Max Email Credits
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          value={formData.maxEmailPurchase}
                          onChange={(e) => setFormData({...formData, maxEmailPurchase: e.target.value})}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="100000"
                        />
                        <p className="text-[9px] text-slate-400 mt-0.5">Maximum credits per purchase</p>
                      </div>

                      {/* Max SMS Credits */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMaximize className="inline mr-1 text-purple-500" /> Max SMS Credits
                        </label>
                        <input
                          type="number"
                          step="1"
                          min="1"
                          value={formData.maxSmsPurchase}
                          onChange={(e) => setFormData({...formData, maxSmsPurchase: e.target.value})}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="100000"
                        />
                        <p className="text-[9px] text-slate-400 mt-0.5">Maximum credits per purchase</p>
                      </div>
                    </div>

                    {/* Row 3: Min Amounts */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      {/* Min Email Amount */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMinus className="inline mr-1 text-emerald-500" /> Min Email Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.minEmailPurchaseAmount}
                            onChange={(e) => setFormData({...formData, minEmailPurchaseAmount: e.target.value})}
                            className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-[9px] text-slate-400 mt-0.5">Min amount in {formData.currencyCode}</p>
                      </div>

                      {/* Min SMS Amount */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMinus className="inline mr-1 text-amber-500" /> Min SMS Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.minSmsPurchaseAmount}
                            onChange={(e) => setFormData({...formData, minSmsPurchaseAmount: e.target.value})}
                            className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-[9px] text-slate-400 mt-0.5">Min amount in {formData.currencyCode}</p>
                      </div>
                    </div>

                    {/* Row 4: Max Amounts - NEW */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                      {/* Max Email Amount */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMaximize className="inline mr-1 text-emerald-500" /> Max Email Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.maxEmailPurchaseAmount}
                            onChange={(e) => setFormData({...formData, maxEmailPurchaseAmount: e.target.value})}
                            className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00 (0 = no limit)"
                          />
                        </div>
                        <p className="text-[9px] text-slate-400 mt-0.5">Max amount in {formData.currencyCode} (0 = no limit)</p>
                      </div>

                      {/* Max SMS Amount */}
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          <FiMaximize className="inline mr-1 text-amber-500" /> Max SMS Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.maxSmsPurchaseAmount}
                            onChange={(e) => setFormData({...formData, maxSmsPurchaseAmount: e.target.value})}
                            className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="0.00 (0 = no limit)"
                          />
                        </div>
                        <p className="text-[9px] text-slate-400 mt-0.5">Max amount in {formData.currencyCode} (0 = no limit)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Change Reason */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Change Reason</label>
                  <input
                    type="text"
                    placeholder="Why is this changing?"
                    value={formData.changeReason}
                    onChange={(e) => setFormData({...formData, changeReason: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                {/* Notes */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 mb-1">Notes</label>
                  <textarea
                    rows="2"
                    placeholder="Additional notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Update Pricing
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* ============================================
            TAB 2: TENANT OVERRIDES
            ============================================ */}
        {activeTab === 'tenants' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Tenant</th>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email</th>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">SMS</th>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Tax</th>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Valid</th>
                    <th className="px-3 sm:px-4 py-2.5 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-3 sm:px-4 py-2.5 text-right text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map((tenant) => {
                    const hasEmailOverride = tenant.pricing.email.overridePrice || tenant.pricing.email.discountPercentage > 0;
                    const hasSmsOverride = tenant.pricing.sms.overridePrice || tenant.pricing.sms.discountPercentage > 0;
                    const hasAnyOverride = hasEmailOverride || hasSmsOverride;
                    const isEmailActive = tenant.pricing.email.validTo ? new Date(tenant.pricing.email.validTo) > new Date() : true;
                    const isSmsActive = tenant.pricing.sms.validTo ? new Date(tenant.pricing.sms.validTo) > new Date() : true;
                    const isActive = isEmailActive || isSmsActive;
                    const hasBulkPricing = tenant.pricing.bulkPricing && tenant.pricing.bulkPricing.length > 0;
                    
                    return (
                      <tr key={tenant.tenantId} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 sm:px-4 py-3">
                          <div>
                            <div className="text-xs font-bold text-slate-900 truncate max-w-[120px] sm:max-w-none">
                              {tenant.organizationName}
                            </div>
                            <div className="text-[10px] text-slate-400 truncate">{tenant.tenantId}</div>
                            {hasBulkPricing && (
                              <span className="text-[9px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full inline-block mt-0.5">
                                Bulk Pricing
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          {hasEmailOverride ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs font-bold text-green-600">
                                {formatPrice(tenant.pricing.email.effectivePrice)}
                              </span>
                              <span className="text-[10px] text-slate-400 line-through">
                                {formatPrice(tenant.pricing.email.basePrice)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">{formatPrice(tenant.pricing.email.basePrice)}</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          {hasSmsOverride ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs font-bold text-green-600">
                                {formatPrice(tenant.pricing.sms.effectivePrice)}
                              </span>
                              <span className="text-[10px] text-slate-400 line-through">
                                {formatPrice(tenant.pricing.sms.basePrice)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">{formatPrice(tenant.pricing.sms.basePrice)}</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                          <span className="text-[10px] text-slate-600">
                            {tenant.pricing.isTaxInclusive !== undefined ? 
                              (tenant.pricing.isTaxInclusive ? 'Inclusive' : 'Exclusive') : 
                              'Global'}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                          {hasAnyOverride ? (
                            <div className="text-[10px]">
                              {tenant.pricing.email.validTo && (
                                <div className={new Date(tenant.pricing.email.validTo) > new Date() ? 'text-green-600' : 'text-red-600'}>
                                  Email: {formatDate(tenant.pricing.email.validTo)}
                                </div>
                              )}
                              {tenant.pricing.sms.validTo && (
                                <div className={new Date(tenant.pricing.sms.validTo) > new Date() ? 'text-green-600' : 'text-red-600'}>
                                  SMS: {formatDate(tenant.pricing.sms.validTo)}
                                </div>
                              )}
                              {!tenant.pricing.email.validTo && !tenant.pricing.sms.validTo && (
                                <span className="text-green-600">Never expires</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400">No override</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3">
                          {hasAnyOverride ? (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${
                              isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                            }`}>
                              {isActive ? 'Active' : 'Expired'}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400">Base</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            <button
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowOverrideDetails(true);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-900 rounded hover:bg-blue-50 transition-colors"
                              title="View Details"
                            >
                              <FiEye className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTenant(tenant);
                                const taxOverride = tenant.pricing.isTaxInclusive !== undefined ? tenant.pricing.isTaxInclusive : null;
                                const taxPctOverride = tenant.pricing.taxPercentage !== undefined ? tenant.pricing.taxPercentage : null;
                                setTenantForm({
                                  ...tenantForm,
                                  isTaxInclusive: taxOverride,
                                  taxPercentage: taxPctOverride,
                                  communicationType: 'email',
                                  discountType: 'percentage',
                                  discountValue: 0,
                                  fixedPrice: null,
                                  validFrom: '',
                                  validTo: '',
                                  description: ''
                                });
                                setShowTenantModal(true);
                              }}
                              className="p-1 text-indigo-600 hover:text-indigo-900 rounded hover:bg-indigo-50 transition-colors"
                              title="Edit Override"
                            >
                              <FiEdit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowBulkModal(true);
                              }}
                              className="p-1 text-purple-600 hover:text-purple-900 rounded hover:bg-purple-50 transition-colors"
                              title="Add Bulk Pricing"
                            >
                              <FiPlus className="w-3.5 h-3.5" />
                            </button>
                            
                            {hasBulkPricing && (
                              <div className="relative group">
                                <button
                                  className="p-1 text-purple-600 hover:text-purple-900 rounded hover:bg-purple-50 transition-colors"
                                  title="Bulk Pricing Actions"
                                >
                                  <FiPackage className="w-3.5 h-3.5" />
                                </button>
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 hidden group-hover:block z-10">
                                  <div className="py-1">
                                    {tenant.pricing.bulkPricing.map((bulk, idx) => (
                                      <div key={idx} className="px-3 py-1.5 hover:bg-slate-50">
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-slate-600">
                                            {bulk.communicationType}: {bulk.minCredits}+ credits
                                          </span>
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => {
                                                setSelectedTenant(tenant);
                                                setSelectedBulkPricing(bulk);
                                                setShowBulkToggleModal(true);
                                              }}
                                              className={`p-0.5 rounded ${
                                                bulk.isActive ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-slate-500'
                                              }`}
                                              title={bulk.isActive ? 'Deactivate' : 'Activate'}
                                            >
                                              <FiPower className="w-3 h-3" />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteBulkPricing(tenant.tenantId, bulk._id)}
                                              className="p-0.5 text-rose-600 hover:text-rose-700 rounded hover:bg-rose-50"
                                              title="Delete"
                                            >
                                              <FiTrash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                        <div className="text-[9px] text-slate-400">
                                          {formatPrice(bulk.specialPrice)}/credit
                                          {bulk.isActive ? ' ✅' : ' ❌'}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <button
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowWalletManager(true);
                              }}
                              className="p-1 text-emerald-600 hover:text-emerald-900 rounded hover:bg-emerald-50 transition-colors"
                              title="Manage Wallet"
                            >
                              <FiCreditCard className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleResetPricing(tenant.tenantId, tenant.organizationName)}
                              className="p-1 text-rose-600 hover:text-rose-900 rounded hover:bg-rose-50 transition-colors"
                              title="Reset to Base"
                            >
                              <FiRotateCcw className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ============================================
            TAB 3: BUNDLES
            ============================================ */}
        {activeTab === 'bundles' && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs text-slate-500">Prepaid packages tenants can purchase</p>
              <button
                onClick={() => {
                  setBundleForm({
                    name: '',
                    description: '',
                    price: 0,
                    emailCredits: 0,
                    smsCredits: 0,
                    taxType: 'inclusive',
                    taxPercentage: 18,
                    taxDescription: 'GST',
                    displayOrder: 0,
                    badge: '',
                    icon: '📦',
                    features: [],
                    recommendedFor: 'all',
                    isActive: true
                  });
                  setShowBundleModal(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors"
              >
                <FiPlus className="w-3.5 h-3.5" />
                Create Bundle
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {bundles.length === 0 ? (
                <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">
                  No bundles created yet. Click "Create Bundle" to get started.
                </div>
              ) : (
                bundles.map((bundle) => (
                  <div key={bundle._id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-2xl">{bundle.icon || '📦'}</span>
                        <h3 className="text-sm font-bold text-slate-900 mt-0.5">{bundle.name}</h3>
                        {bundle.badge && (
                          <span className="inline-block px-2 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-800 rounded-full mt-0.5">
                            {bundle.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleToggleBundle(bundle._id, bundle.isActive)}
                          className={`p-1 rounded ${bundle.isActive ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={bundle.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {bundle.isActive ? <FiToggleLeft className="w-4 h-4" /> : <FiToggleRight className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBundle(bundle);
                            setBundleForm({
                              name: bundle.name,
                              description: bundle.description || '',
                              price: bundle.price,
                              emailCredits: bundle.emailCredits,
                              smsCredits: bundle.smsCredits,
                              taxType: bundle.taxType || 'inclusive',
                              taxPercentage: bundle.taxPercentage || 18,
                              taxDescription: bundle.taxDescription || 'GST',
                              displayOrder: bundle.displayOrder || 0,
                              badge: bundle.badge || '',
                              icon: bundle.icon || '📦',
                              features: bundle.features || [],
                              recommendedFor: bundle.recommendedFor || 'all',
                              isActive: bundle.isActive
                            });
                            setShowBundleModal(true);
                          }}
                          className="p-1 text-indigo-600 hover:text-indigo-900 rounded hover:bg-indigo-50 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteBundle(bundle._id, bundle.name)}
                          className="p-1 text-rose-600 hover:text-rose-900 rounded hover:bg-rose-50 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{bundle.description || 'No description'}</p>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div>
                        <p className="text-base font-black text-slate-900">{formatPrice(bundle.price)}</p>
                        <p className="text-[9px] text-slate-400">
                          {bundle.taxDescription} {bundle.taxPercentage}% 
                          ({bundle.taxType === 'inclusive' ? 'Inclusive' : bundle.taxType === 'exclusive' ? 'Exclusive' : 'None'})
                        </p>
                      </div>
                      <div className="text-right text-[10px] font-bold">
                        <p className="text-blue-600">{bundle.emailCredits} emails</p>
                        <p className="text-purple-600">{bundle.smsCredits} SMS</p>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex flex-wrap gap-1">
                      {bundle.features && bundle.features.slice(0, 2).map((feature, idx) => (
                        <span key={idx} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                          {feature}
                        </span>
                      ))}
                      {bundle.features && bundle.features.length > 2 && (
                        <span className="text-[9px] text-slate-400">+{bundle.features.length - 2}</span>
                      )}
                    </div>
                    
                    <div className="mt-2.5 pt-2.5 border-t border-slate-100 flex justify-between items-center">
                      <span className={`text-[9px] font-bold ${bundle.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {bundle.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-[9px] text-slate-400">
                        {bundle.recommendedFor !== 'all' ? bundle.recommendedFor : 'All'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* ============================================
            TAB 4: TENANT WALLETS
            ============================================ */}
        {activeTab === 'wallets' && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <TenantWalletManager />
          </div>
        )}
        
        {/* ============================================
            TAB 5: AUDIT LOG
            ============================================ */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Recent Activity</h3>
              <span className="text-xs text-slate-400">{auditLogs.length} entries</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {auditLogs.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">No audit logs found</div>
              ) : (
                auditLogs.map((log) => (
                  <div key={log._id} className="px-3 sm:px-4 py-3 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                        {log.action.includes('bundle') && <FiPackage className="w-3.5 h-3.5" />}
                        {log.action === 'price_updated' && <FiDollarSign className="w-3.5 h-3.5" />}
                        {log.action === 'discount_applied' && <FiTag className="w-3.5 h-3.5" />}
                        {log.action === 'discount_removed' && <FiTrash2 className="w-3.5 h-3.5" />}
                        {log.action === 'tenant_pricing_reset' && <FiRotateCcw className="w-3.5 h-3.5" />}
                        {log.action === 'credits_adjusted' && <FiTrendingUp className="w-3.5 h-3.5" />}
                        {log.action === 'credits_refunded' && <FiRotateCcw className="w-3.5 h-3.5" />}
                        {log.action === 'credits_cleared' && <FiTrash2 className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-xs font-bold text-slate-900">
                            {formatAction(log.action)}
                          </p>
                          <span className="text-[10px] text-slate-400">{formatDate(log.createdAt)}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          <span className="font-medium">{log.performedBy}</span>
                          {log.targetTenantId && (
                            <span className="text-slate-400"> • {log.targetTenantId}</span>
                          )}
                          {log.details?.bundleId && (
                            <span className="text-slate-400"> • {log.details.name}</span>
                          )}
                        </p>
                        {log.details && (
                          <div className="mt-1.5 bg-slate-50 rounded-lg p-2 text-xs">
                            {log.action === 'price_updated' && log.details.changes && (
                              <div className="space-y-0.5">
                                {log.details.changes.emailPricePerCredit && (
                                  <p>
                                    <span className="text-slate-500">Email:</span>
                                    <span className="line-through text-rose-500 ml-1">{formatPrice(log.details.changes.emailPricePerCredit.old)}</span>
                                    <FiArrowRight className="inline mx-1 text-slate-400 w-3 h-3" />
                                    <span className="text-emerald-600">{formatPrice(log.details.changes.emailPricePerCredit.new)}</span>
                                  </p>
                                )}
                                {log.details.changes.smsPricePerCredit && (
                                  <p>
                                    <span className="text-slate-500">SMS:</span>
                                    <span className="line-through text-rose-500 ml-1">{formatPrice(log.details.changes.smsPricePerCredit.old)}</span>
                                    <FiArrowRight className="inline mx-1 text-slate-400 w-3 h-3" />
                                    <span className="text-emerald-600">{formatPrice(log.details.changes.smsPricePerCredit.new)}</span>
                                  </p>
                                )}
                                {log.details.changes.isTaxInclusive && (
                                  <p>
                                    <span className="text-slate-500">Tax Type:</span>
                                    <span className="text-rose-500 ml-1">{log.details.changes.isTaxInclusive.old ? 'Inclusive' : 'Exclusive'}</span>
                                    <FiArrowRight className="inline mx-1 text-slate-400 w-3 h-3" />
                                    <span className="text-emerald-600">{log.details.changes.isTaxInclusive.new ? 'Inclusive' : 'Exclusive'}</span>
                                  </p>
                                )}
                                {log.details.changes.minEmailPurchaseAmount && (
                                  <p>
                                    <span className="text-slate-500">Min Email Amount:</span>
                                    <span className="line-through text-rose-500 ml-1">{formatPrice(log.details.changes.minEmailPurchaseAmount.old)}</span>
                                    <FiArrowRight className="inline mx-1 text-slate-400 w-3 h-3" />
                                    <span className="text-emerald-600">{formatPrice(log.details.changes.minEmailPurchaseAmount.new)}</span>
                                  </p>
                                )}
                                {log.details.changes.maxEmailPurchaseAmount && (
                                  <p>
                                    <span className="text-slate-500">Max Email Amount:</span>
                                    <span className="line-through text-rose-500 ml-1">{formatPrice(log.details.changes.maxEmailPurchaseAmount.old)}</span>
                                    <FiArrowRight className="inline mx-1 text-slate-400 w-3 h-3" />
                                    <span className="text-emerald-600">{formatPrice(log.details.changes.maxEmailPurchaseAmount.new)}</span>
                                  </p>
                                )}
                              </div>
                            )}
                            {log.action === 'discount_applied' && log.details.overrides && (
                              <div className="text-slate-600">
                                {log.details.overrides.map((override, idx) => (
                                  <span key={idx}>
                                    {override.communicationType}: 
                                    {override.discountType === 'percentage' ? ` ${override.discountValue}% off` :
                                     override.discountType === 'fixed' ? ` ${formatPrice(override.discountValue)} off` :
                                     ' No discount'}
                                    {override.validTo && ` until ${formatDate(override.validTo)}`}
                                    {idx < log.details.overrides.length - 1 && ', '}
                                  </span>
                                ))}
                                {log.details.taxSettings && (
                                  <span className="block text-slate-400 mt-1">
                                    Tax: {log.details.taxSettings.isTaxInclusive !== 'not_changed' ? (log.details.taxSettings.isTaxInclusive ? 'Inclusive' : 'Exclusive') : 'Not changed'}
                                    {log.details.taxSettings.taxPercentage !== 'not_changed' && `, ${log.details.taxSettings.taxPercentage}%`}
                                  </span>
                                )}
                              </div>
                            )}
                            {log.action === 'bundle_created' && (
                              <p className="text-slate-600">Created: {log.details.name} ({formatPrice(log.details.price)})</p>
                            )}
                            {log.action === 'bundle_updated' && (
                              <p className="text-slate-500">Updated: {log.details.name}</p>
                            )}
                            {log.action === 'bundle_deleted' && (
                              <p className="text-slate-500">Deleted: {log.details.name}</p>
                            )}
                            {log.action === 'bundle_toggled' && (
                              <p className="text-slate-500">
                                {log.details.name} {log.details.isActive ? 'activated' : 'deactivated'}
                              </p>
                            )}
                            {log.action === 'tenant_pricing_reset' && (
                              <p className="text-slate-500">All pricing overrides reset to base</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* ============================================
          MODALS (Same as before)
          ============================================ */}
      
      {/* Tenant Override Details Modal */}
      {showOverrideDetails && selectedTenant && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">
                {selectedTenant.organizationName}
              </h3>
              <button
                onClick={() => setShowOverrideDetails(false)}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Email Pricing */}
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <FiMail className="text-blue-500" /> Email Pricing
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Base:</span>
                    <span className="ml-1 font-bold text-slate-900">{formatPrice(selectedTenant.pricing.email.basePrice)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Effective:</span>
                    <span className={`ml-1 font-bold ${selectedTenant.pricing.email.effectivePrice !== selectedTenant.pricing.email.basePrice ? 'text-emerald-600' : 'text-slate-900'}`}>
                      {formatPrice(selectedTenant.pricing.email.effectivePrice)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Discount:</span>
                    <span className="ml-1 font-bold">
                      {selectedTenant.pricing.email.discountPercentage > 0 
                        ? `${selectedTenant.pricing.email.discountPercentage}%` 
                        : 'None'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Valid:</span>
                    <span className={`ml-1 font-bold ${selectedTenant.pricing.email.validTo ? (new Date(selectedTenant.pricing.email.validTo) > new Date() ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-400'}`}>
                      {selectedTenant.pricing.email.validTo ? formatDate(selectedTenant.pricing.email.validTo) : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* SMS Pricing */}
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <FiMessageSquare className="text-purple-500" /> SMS Pricing
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Base:</span>
                    <span className="ml-1 font-bold text-slate-900">{formatPrice(selectedTenant.pricing.sms.basePrice)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Effective:</span>
                    <span className={`ml-1 font-bold ${selectedTenant.pricing.sms.effectivePrice !== selectedTenant.pricing.sms.basePrice ? 'text-emerald-600' : 'text-slate-900'}`}>
                      {formatPrice(selectedTenant.pricing.sms.effectivePrice)}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Discount:</span>
                    <span className="ml-1 font-bold">
                      {selectedTenant.pricing.sms.discountPercentage > 0 
                        ? `${selectedTenant.pricing.sms.discountPercentage}%` 
                        : 'None'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Valid:</span>
                    <span className={`ml-1 font-bold ${selectedTenant.pricing.sms.validTo ? (new Date(selectedTenant.pricing.sms.validTo) > new Date() ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-400'}`}>
                      {selectedTenant.pricing.sms.validTo ? formatDate(selectedTenant.pricing.sms.validTo) : 'Never'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tax Information */}
              <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                  <FiPercent className="text-amber-500" /> Tax Configuration
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-500">Tax Type:</span>
                    <span className={`ml-1 font-bold ${
                      selectedTenant.pricing.isTaxInclusive !== undefined 
                        ? (selectedTenant.pricing.isTaxInclusive ? 'text-emerald-600' : 'text-blue-600')
                        : 'text-slate-400'
                    }`}>
                      {selectedTenant.pricing.isTaxInclusive !== undefined 
                        ? (selectedTenant.pricing.isTaxInclusive ? 'Inclusive ✅' : 'Exclusive ➕')
                        : 'Using Global'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Tax %:</span>
                    <span className={`ml-1 font-bold ${
                      selectedTenant.pricing.taxPercentage !== undefined 
                        ? 'text-emerald-600'
                        : 'text-slate-400'
                    }`}>
                      {selectedTenant.pricing.taxPercentage !== undefined 
                        ? `${selectedTenant.pricing.taxPercentage}%`
                        : 'Using Global'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Bulk Pricing */}
              {selectedTenant.pricing.bulkPricing && selectedTenant.pricing.bulkPricing.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
                    <FiPlus className="text-purple-500" /> Bulk Pricing
                  </h4>
                  <div className="space-y-1.5">
                    {selectedTenant.pricing.bulkPricing.map((bulk, idx) => (
                      <div key={idx} className="bg-slate-50 rounded p-2 text-xs flex justify-between items-center">
                        <div>
                          <span className="font-bold">{bulk.communicationType}</span>
                          <span className="text-slate-500 ml-2">{bulk.minCredits}+ credits</span>
                          <span className="text-emerald-600 font-bold ml-2">{formatPrice(bulk.specialPrice)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] font-bold ${bulk.isActive ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {bulk.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedTenant(selectedTenant);
                              setSelectedBulkPricing(bulk);
                              setShowBulkToggleModal(true);
                            }}
                            className={`p-0.5 rounded ${
                              bulk.isActive ? 'text-green-600 hover:text-green-700' : 'text-slate-400 hover:text-slate-500'
                            }`}
                            title={bulk.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <FiPower className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleDeleteBulkPricing(selectedTenant.tenantId, bulk._id)}
                            className="p-0.5 text-rose-600 hover:text-rose-700 rounded hover:bg-rose-50"
                            title="Delete"
                          >
                            <FiTrash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  onClick={() => setShowOverrideDetails(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowOverrideDetails(false);
                    setShowTenantModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition-colors"
                >
                  Edit Override
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tenant Override Modal */}
      {showTenantModal && selectedTenant && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">
                Override: {selectedTenant.organizationName}
              </h3>
              <button
                onClick={() => setShowTenantModal(false)}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Communication Type</label>
                <select
                  value={tenantForm.communicationType}
                  onChange={(e) => setTenantForm({...tenantForm, communicationType: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Discount Type</label>
                <select
                  value={tenantForm.discountType}
                  onChange={(e) => setTenantForm({...tenantForm, discountType: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="none">None</option>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              
              {tenantForm.discountType !== 'none' && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    {tenantForm.discountType === 'percentage' ? 'Discount %' : `Fixed Discount (${getCurrencySymbol()})`}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tenantForm.discountValue}
                    onChange={(e) => setTenantForm({...tenantForm, discountValue: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g., Bulk purchase discount"
                  value={tenantForm.description}
                  onChange={(e) => setTenantForm({...tenantForm, description: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Valid From</label>
                  <input
                    type="date"
                    value={tenantForm.validFrom}
                    onChange={(e) => setTenantForm({...tenantForm, validFrom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Valid To</label>
                  <input
                    type="date"
                    value={tenantForm.validTo}
                    onChange={(e) => setTenantForm({...tenantForm, validTo: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Tax Override Section */}
              <div className="border-t border-slate-200 pt-3 mt-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tax Override (Optional)</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Tax Type</label>
                    <select
                      value={tenantForm.isTaxInclusive !== null && tenantForm.isTaxInclusive !== undefined ? String(tenantForm.isTaxInclusive) : ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTenantForm({
                          ...tenantForm, 
                          isTaxInclusive: val === '' ? null : val === 'true'
                        });
                      }}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Use Global Setting</option>
                      <option value="true">Inclusive</option>
                      <option value="false">Exclusive</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Tax %</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="Leave empty for global"
                      value={tenantForm.taxPercentage !== null && tenantForm.taxPercentage !== undefined ? tenantForm.taxPercentage : ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setTenantForm({
                          ...tenantForm, 
                          taxPercentage: val === '' ? null : parseFloat(val)
                        });
                      }}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-[9px] text-slate-400 mt-0.5">Leave empty to use global tax percentage</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowTenantModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleTenantOverride(selectedTenant.tenantId)}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Save Override'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Pricing Modal */}
      {showBulkModal && selectedTenant && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">
                Bulk Pricing: {selectedTenant.organizationName}
              </h3>
              <button
                onClick={() => setShowBulkModal(false)}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Communication Type</label>
                <select
                  value={bulkForm.communicationType}
                  onChange={(e) => setBulkForm({...bulkForm, communicationType: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Minimum Credits</label>
                <input
                  type="number"
                  step="100"
                  min="0"
                  value={bulkForm.minCredits}
                  onChange={(e) => setBulkForm({...bulkForm, minCredits: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Special Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                    {getCurrencySymbol()}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkForm.specialPrice}
                    onChange={(e) => setBulkForm({...bulkForm, specialPrice: e.target.value})}
                    className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  placeholder="e.g., Annual bulk rate"
                  value={bulkForm.description}
                  onChange={(e) => setBulkForm({...bulkForm, description: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Valid From</label>
                  <input
                    type="date"
                    value={bulkForm.validFrom}
                    onChange={(e) => setBulkForm({...bulkForm, validFrom: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Valid To</label>
                  <input
                    type="date"
                    value={bulkForm.validTo}
                    onChange={(e) => setBulkForm({...bulkForm, validTo: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleBulkPricing(selectedTenant.tenantId)}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : 'Add Bulk Pricing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bundle Modal */}
      {showBundleModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">
                {selectedBundle ? 'Edit Bundle' : 'Create Bundle'}
              </h3>
              <button
                onClick={() => {
                  setShowBundleModal(false);
                  setSelectedBundle(null);
                }}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <form onSubmit={selectedBundle ? handleUpdateBundle : handleCreateBundle} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={bundleForm.name}
                    onChange={(e) => setBundleForm({...bundleForm, name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Starter"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Icon</label>
                  <input
                    type="text"
                    value={bundleForm.icon}
                    onChange={(e) => setBundleForm({...bundleForm, icon: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="📦"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Description</label>
                <textarea
                  rows="2"
                  value={bundleForm.description}
                  onChange={(e) => setBundleForm({...bundleForm, description: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Brief description"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    value={bundleForm.price}
                    onChange={(e) => setBundleForm({...bundleForm, price: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Email Credits *</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={bundleForm.emailCredits}
                    onChange={(e) => setBundleForm({...bundleForm, emailCredits: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">SMS Credits *</label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={bundleForm.smsCredits}
                    onChange={(e) => setBundleForm({...bundleForm, smsCredits: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Tax Type</label>
                  <select
                    value={bundleForm.taxType}
                    onChange={(e) => setBundleForm({...bundleForm, taxType: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="inclusive">Inclusive</option>
                    <option value="exclusive">Exclusive</option>
                    <option value="none">None</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Tax %</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={bundleForm.taxPercentage}
                    onChange={(e) => setBundleForm({...bundleForm, taxPercentage: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Badge</label>
                  <input
                    type="text"
                    value={bundleForm.badge}
                    onChange={(e) => setBundleForm({...bundleForm, badge: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder="Popular"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={bundleForm.displayOrder}
                    onChange={(e) => setBundleForm({...bundleForm, displayOrder: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Features (comma separated)</label>
                <input
                  type="text"
                  value={bundleForm.features.join(', ')}
                  onChange={(e) => setBundleForm({...bundleForm, features: e.target.value.split(',').map(f => f.trim()).filter(f => f)})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  placeholder="Priority Support, Analytics"
                />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Recommended For</label>
                <select
                  value={bundleForm.recommendedFor}
                  onChange={(e) => setBundleForm({...bundleForm, recommendedFor: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Institutions</option>
                  <option value="schools">Schools</option>
                  <option value="colleges">Colleges</option>
                  <option value="universities">Universities</option>
                  <option value="coaching">Coaching Centers</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={bundleForm.isActive}
                  onChange={(e) => setBundleForm({...bundleForm, isActive: e.target.checked})}
                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                />
                <label className="text-xs font-bold text-slate-700">Active</label>
              </div>
              
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowBundleModal(false);
                    setSelectedBundle(null);
                  }}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></span>
                      {selectedBundle ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    selectedBundle ? 'Update Bundle' : 'Create Bundle'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Wallet Manager Modal */}
      {showWalletManager && selectedTenant && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-sm font-bold text-slate-900">
                Wallet Management: {selectedTenant.organizationName}
              </h3>
              <button
                onClick={() => setShowWalletManager(false)}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-4">
              <TenantWalletManager 
                tenantId={selectedTenant.tenantId} 
                onClose={() => setShowWalletManager(false)} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Bulk Pricing Toggle Modal */}
      {showBulkToggleModal && selectedTenant && selectedBulkPricing && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h3 className="text-sm font-bold text-slate-900">
                {selectedBulkPricing.isActive ? 'Deactivate' : 'Activate'} Bulk Pricing
              </h3>
              <button
                onClick={() => {
                  setShowBulkToggleModal(false);
                  setSelectedBulkPricing(null);
                }}
                className="p-1 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
              >
                <FiX className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-2 text-xs">
              <p className="text-slate-600">
                <span className="font-bold">Tenant:</span> {selectedTenant.organizationName}
              </p>
              <p className="text-slate-600">
                <span className="font-bold">Type:</span> {selectedBulkPricing.communicationType}
              </p>
              <p className="text-slate-600">
                <span className="font-bold">Min Credits:</span> {selectedBulkPricing.minCredits}
              </p>
              <p className="text-slate-600">
                <span className="font-bold">Special Price:</span> {formatPrice(selectedBulkPricing.specialPrice)}
              </p>
              <p className="text-slate-600">
                <span className="font-bold">Status:</span>{' '}
                <span className={selectedBulkPricing.isActive ? 'text-emerald-600 font-bold' : 'text-rose-600 font-bold'}>
                  {selectedBulkPricing.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              <FiAlertCircle className="inline w-4 h-4 mr-1" />
              {selectedBulkPricing.isActive 
                ? 'This will deactivate the bulk pricing. Tenants will no longer see this offer.'
                : 'This will activate the bulk pricing. Tenants will be able to see and use this offer.'
              }
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => {
                  setShowBulkToggleModal(false);
                  setSelectedBulkPricing(null);
                }}
                className="w-full py-2.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleToggleBulkPricing(selectedTenant.tenantId, selectedBulkPricing._id);
                }}
                disabled={saving}
                className={`w-full py-2.5 rounded-lg text-xs font-bold text-white transition-colors ${
                  selectedBulkPricing.isActive 
                    ? 'bg-rose-600 hover:bg-rose-700' 
                    : 'bg-emerald-600 hover:bg-emerald-700'
                } disabled:opacity-50`}
              >
                {saving ? 'Processing...' : (selectedBulkPricing.isActive ? 'Deactivate' : 'Activate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingManagement;